import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import { MapMarker } from "./MapMarker";

interface MapPopupProps {
  map: mapboxgl.Map | null;
  coordinates: [number, number] | null;
  children: React.ReactNode;
  onClose?: () => void;
  offset?: number;
  /** Optional icon to show inside the marker */
  markerIcon?: React.ReactNode;
}

/**
 * Renders React content inside a Mapbox popup using portals.
 * Following the pattern from Mapbox's dynamic-markers-react tutorial.
 *
 * Keep this component always mounted - pass null coordinates to hide.
 */
export function MapPopup({ map, coordinates, children, onClose, offset = 20, markerIcon }: MapPopupProps) {
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const contentRef = useRef<HTMLDivElement>(document.createElement("div"));

  // Create popup once when map is available
  useEffect(() => {
    if (!map) return;

    popupRef.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset,
      className: "map-popup-portal",
    });

    return () => {
      popupRef.current?.remove();
    };
  }, [map, offset]);

  // Show/hide popup when coordinates change
  useEffect(() => {
    if (!popupRef.current || !map) return;

    if (!coordinates) {
      popupRef.current.remove();
      return;
    }

    popupRef.current.setLngLat(coordinates).setDOMContent(contentRef.current).addTo(map);
  }, [coordinates, map]);

  // Close on Escape key
  useEffect(() => {
    if (!coordinates) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [coordinates, onClose]);

  return (
    <>
      <MapMarker map={map} coordinates={coordinates} size="sm">
        {markerIcon}
      </MapMarker>
      {createPortal(<div className="animate-in fade-in zoom-in-50 duration-200">{children}</div>, contentRef.current)}
    </>
  );
}
